import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Users, Image, Check, X, Clock, Eye, Trash2, Settings, Video, ImageIcon, Save, Loader2, Mail, Link, MessageSquare, Send, CheckSquare, Square, Download, Flame, Plus, Minus } from 'lucide-react';
import { toast } from './ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('registrations');
  const [registrations, setRegistrations] = useState([]);
  const [pendingScreenshots, setPendingScreenshots] = useState([]);
  const [allScreenshots, setAllScreenshots] = useState([]);
  const [siteContent, setSiteContent] = useState([]);
  const [editingContent, setEditingContent] = useState({});
  const [savingContent, setSavingContent] = useState({});
  const [processingBeta, setProcessingBeta] = useState({});
  const [selectedBetas, setSelectedBetas] = useState(new Set());
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  const [deletingBeta, setDeletingBeta] = useState({});
  const [elements, setElements] = useState([]);
  const [editingElements, setEditingElements] = useState({});
  const [savingElement, setSavingElement] = useState({});

  useEffect(() => {
    const adminAuth = sessionStorage.getItem('adminAuth');
    if (adminAuth === 'true') {
      setIsLoggedIn(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [regRes, pendingRes, allRes, contentRes, elementsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/beta-registrations`),
        fetch(`${API_URL}/api/admin/screenshots/pending`),
        fetch(`${API_URL}/api/admin/screenshots/all`),
        fetch(`${API_URL}/api/site-content`),
        fetch(`${API_URL}/api/elements`)
      ]);
      
      const regData = await regRes.json();
      const pendingData = await pendingRes.json();
      const allData = await allRes.json();
      const contentData = await contentRes.json();
      const elementsData = await elementsRes.json();
      
      if (regData.success) setRegistrations(regData.registrations);
      if (pendingData.success) setPendingScreenshots(pendingData.screenshots);
      if (allData.success) setAllScreenshots(allData.screenshots);
      if (contentData.success) {
        setSiteContent(contentData.content);
        const initialEditing = {};
        contentData.content.forEach(item => {
          initialEditing[item.key] = item.value;
        });
        setEditingContent(initialEditing);
      }
      if (elementsData.success) {
        setElements(elementsData.elements);
        const initialElements = {};
        elementsData.elements.forEach(el => {
          initialElements[el.id] = { ...el };
        });
        setEditingElements(initialElements);
      }
      // Clear selections after refresh
      setSelectedBetas(new Set());
    } catch (error) {
      // Silently handle fetch errors - data will remain empty
    }
  };

  const toggleBetaSelection = (id) => {
    setSelectedBetas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleAllBetas = (pendingList) => {
    if (selectedBetas.size === pendingList.length) {
      setSelectedBetas(new Set());
    } else {
      setSelectedBetas(new Set(pendingList.map(r => r.id)));
    }
  };

  const handleSendInvites = async () => {
    if (selectedBetas.size === 0) {
      toast.error('Please select at least one beta tester');
      return;
    }

    setIsSendingInvites(true);
    let successCount = 0;
    let failCount = 0;

    for (const regId of selectedBetas) {
      try {
        const response = await fetch(`${API_URL}/api/admin/beta/action`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registration_id: regId, action: 'approve' })
        });
        
        const data = await response.json();
        if (data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} invite${successCount > 1 ? 's' : ''} sent successfully!`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} invite${failCount > 1 ? 's' : ''} failed to send`);
    }

    setIsSendingInvites(false);
    fetchData();
  };

  const handleRejectSelected = async () => {
    if (selectedBetas.size === 0) {
      toast.error('Please select at least one registration');
      return;
    }

    if (!window.confirm(`Reject ${selectedBetas.size} selected registration(s)?`)) return;

    setIsSendingInvites(true);
    let successCount = 0;

    for (const regId of selectedBetas) {
      try {
        const response = await fetch(`${API_URL}/api/admin/beta/action`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registration_id: regId, action: 'reject' })
        });
        
        const data = await response.json();
        if (data.success) successCount++;
      } catch (error) {
        // Continue with next registration on error
        continue;
      }
    }

    toast.success(`${successCount} registration(s) rejected`);
    setIsSendingInvites(false);
    fetchData();
  };

  const handleBetaAction = async (registrationId, action) => {
    setProcessingBeta(prev => ({ ...prev, [registrationId]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/admin/beta/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, action })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setProcessingBeta(prev => ({ ...prev, [registrationId]: false }));
    }
  };

  const handleDeleteBeta = async (registrationId) => {
    if (!window.confirm('Are you sure you want to delete this beta tester?')) return;
    
    setDeletingBeta(prev => ({ ...prev, [registrationId]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/admin/beta/${registrationId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Beta tester deleted');
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setDeletingBeta(prev => ({ ...prev, [registrationId]: false }));
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/admin/beta/export-csv`, '_blank');
    toast.success('Downloading CSV...');
  };

  // Element editing functions
  const updateElementField = (elementId, field, value) => {
    setEditingElements(prev => ({
      ...prev,
      [elementId]: { ...prev[elementId], [field]: value }
    }));
  };

  const addAbility = (elementId) => {
    setEditingElements(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        abilities: [...(prev[elementId]?.abilities || []), { name: 'New Ability', icon: '' }]
      }
    }));
  };

  const removeAbility = (elementId, index) => {
    setEditingElements(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        abilities: prev[elementId].abilities.filter((_, i) => i !== index)
      }
    }));
  };

  const updateAbilityName = (elementId, index, value) => {
    setEditingElements(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        abilities: prev[elementId].abilities.map((a, i) => 
          i === index ? { ...a, name: value } : a
        )
      }
    }));
  };

  const updateAbilityIcon = (elementId, index, value) => {
    setEditingElements(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        abilities: prev[elementId].abilities.map((a, i) => 
          i === index ? { ...a, icon: value } : a
        )
      }
    }));
  };

  const handleSaveElement = async (elementId) => {
    setSavingElement(prev => ({ ...prev, [elementId]: true }));
    
    try {
      const elementData = editingElements[elementId];
      const response = await fetch(`${API_URL}/api/admin/elements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(elementData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Save failed');
    } finally {
      setSavingElement(prev => ({ ...prev, [elementId]: false }));
    }
  };

  const hasElementChanged = (elementId) => {
    const original = elements.find(e => e.id === elementId);
    const edited = editingElements[elementId];
    if (!original || !edited) return false;
    return JSON.stringify(original) !== JSON.stringify(edited);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        sessionStorage.setItem('adminAuth', 'true');
        setIsLoggedIn(true);
        toast.success('Welcome, Admin!');
        fetchData();
      } else {
        toast.error('Invalid credentials');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
  };

  const handleScreenshotAction = async (screenshotId, action) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/screenshots/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ screenshot_id: screenshotId, action })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleDeleteScreenshot = async (screenshotId) => {
    if (!window.confirm('Are you sure you want to delete this screenshot?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/screenshots/${screenshotId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Screenshot deleted');
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleSaveContent = async (key) => {
    setSavingContent(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/admin/site-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: editingContent[key] })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Save failed');
    } finally {
      setSavingContent(prev => ({ ...prev, [key]: false }));
    }
  };

  const getContentIcon = (key) => {
    if (key.includes('video')) return <Video size={18} className="text-purple-400" />;
    if (key.includes('element')) return <ImageIcon size={18} className="text-orange-400" />;
    if (key.includes('beta_game_link')) return <Link size={18} className="text-green-400" />;
    if (key.includes('beta_email')) return <MessageSquare size={18} className="text-cyan-400" />;
    return <ImageIcon size={18} className="text-blue-400" />;
  };

  const pendingRegistrations = registrations.filter(r => r.status === 'pending' || !r.status);
  const approvedRegistrations = registrations.filter(r => r.status === 'approved');
  const rejectedRegistrations = registrations.filter(r => r.status === 'rejected');

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-page-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-card-bg border border-border-light mb-4">
              <Lock className="text-brand-primary" size={32} />
            </div>
            <h1 className="heading-3 text-brand-primary">Admin Access</h1>
            <p className="body-small text-text-secondary mt-2">Fight for Erebus Dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="beta-form">
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                placeholder="Enter username"
                data-testid="admin-username"
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="Enter password"
                data-testid="admin-password"
                required
              />
            </div>
            
            <button 
              type="submit" 
              className="btn-primary w-full"
              data-testid="admin-login-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-4 text-text-secondary hover:text-brand-primary transition-colors body-small"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-bg">
      {/* Admin Header */}
      <header className="bg-card-bg border-b border-border-light sticky top-0 z-50">
        <div className="container-custom py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="heading-4 text-brand-primary">Admin Dashboard</h1>
            <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm">
              Fight for Erebus
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="text-text-secondary hover:text-brand-primary transition-colors body-small"
            >
              View Site
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-text-secondary hover:text-red-500 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
              <span className="body-small">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="container-custom py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => setActiveTab('registrations')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
              activeTab === 'registrations' 
                ? 'bg-brand-primary text-page-bg font-semibold' 
                : 'bg-card-bg text-text-secondary hover:text-brand-primary border border-border-light'
            }`}
            data-testid="tab-registrations"
          >
            <Users size={18} />
            Beta ({pendingRegistrations.length} pending)
          </button>
          <button
            onClick={() => setActiveTab('screenshots')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
              activeTab === 'screenshots' 
                ? 'bg-brand-primary text-page-bg font-semibold' 
                : 'bg-card-bg text-text-secondary hover:text-brand-primary border border-border-light'
            }`}
            data-testid="tab-screenshots"
          >
            <Image size={18} />
            Screenshots ({pendingScreenshots.length} pending)
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
              activeTab === 'content' 
                ? 'bg-brand-primary text-page-bg font-semibold' 
                : 'bg-card-bg text-text-secondary hover:text-brand-primary border border-border-light'
            }`}
            data-testid="tab-content"
          >
            <Settings size={18} />
            Site Content
          </button>
          <button
            onClick={() => setActiveTab('elements')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
              activeTab === 'elements' 
                ? 'bg-brand-primary text-page-bg font-semibold' 
                : 'bg-card-bg text-text-secondary hover:text-brand-primary border border-border-light'
            }`}
            data-testid="tab-elements"
          >
            <Flame size={18} />
            Elements
          </button>
        </div>

        {/* Beta Registrations Tab */}
        {activeTab === 'registrations' && (
          <div className="space-y-8">
            {/* Pending Registrations */}
            <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
              <div className="p-6 border-b border-border-light flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="text-yellow-500" size={20} />
                  <div>
                    <h2 className="heading-5">Pending Approval</h2>
                    <p className="body-small text-text-secondary mt-1">
                      {pendingRegistrations.length} users waiting for beta approval
                      {selectedBetas.size > 0 && (
                        <span className="text-brand-primary ml-2">
                          ({selectedBetas.size} selected)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {pendingRegistrations.length > 0 && (
                  <button
                    onClick={() => toggleAllBetas(pendingRegistrations)}
                    className="text-sm text-text-secondary hover:text-brand-primary transition-colors"
                  >
                    {selectedBetas.size === pendingRegistrations.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              
              {pendingRegistrations.length === 0 ? (
                <div className="p-12 text-center text-text-secondary">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No pending registrations</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-page-bg">
                        <tr>
                          <th className="w-12 p-4"></th>
                          <th className="text-left p-4 caption text-text-secondary">Email</th>
                          <th className="text-left p-4 caption text-text-secondary">Registered At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingRegistrations.map((reg) => (
                          <tr 
                            key={reg.id} 
                            className={`border-t border-border-light cursor-pointer transition-colors ${
                              selectedBetas.has(reg.id) ? 'bg-brand-primary/10' : 'hover:bg-page-bg/50'
                            }`}
                            onClick={() => toggleBetaSelection(reg.id)}
                          >
                            <td className="p-4">
                              <div className="flex items-center justify-center">
                                {selectedBetas.has(reg.id) ? (
                                  <CheckSquare size={20} className="text-brand-primary" />
                                ) : (
                                  <Square size={20} className="text-text-secondary" />
                                )}
                              </div>
                            </td>
                            <td className="p-4 body-small text-text-primary">{reg.email}</td>
                            <td className="p-4 body-small text-text-secondary">
                              {new Date(reg.registered_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Action Bar */}
                  <div className="p-4 border-t border-border-light bg-page-bg/50 flex items-center justify-between">
                    <p className="body-small text-text-secondary">
                      {selectedBetas.size > 0 
                        ? `${selectedBetas.size} tester${selectedBetas.size > 1 ? 's' : ''} selected`
                        : 'Click rows to select testers'
                      }
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleRejectSelected}
                        disabled={selectedBetas.size === 0 || isSendingInvites}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                          selectedBetas.size > 0
                            ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                            : 'bg-card-bg text-text-secondary cursor-not-allowed'
                        }`}
                        data-testid="reject-selected-btn"
                      >
                        <X size={16} />
                        Reject Selected
                      </button>
                      <button
                        onClick={handleSendInvites}
                        disabled={selectedBetas.size === 0 || isSendingInvites}
                        className={`flex items-center gap-2 px-6 py-2 rounded-full font-semibold transition-colors ${
                          selectedBetas.size > 0
                            ? 'bg-brand-primary text-page-bg hover:bg-brand-primary/90'
                            : 'bg-card-bg text-text-secondary cursor-not-allowed'
                        }`}
                        data-testid="send-invites-btn"
                      >
                        {isSendingInvites ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Send size={18} />
                        )}
                        {isSendingInvites 
                          ? `Sending ${selectedBetas.size} invite${selectedBetas.size > 1 ? 's' : ''}...`
                          : `Send ${selectedBetas.size > 0 ? selectedBetas.size : ''} Invite${selectedBetas.size !== 1 ? 's' : ''}`
                        }
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Approved Registrations */}
            <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
              <div className="p-6 border-b border-border-light flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="text-green-500" size={20} />
                  <div>
                    <h2 className="heading-5">Approved Beta Testers</h2>
                    <p className="body-small text-text-secondary mt-1">
                      {approvedRegistrations.length} users have been approved and emailed
                    </p>
                  </div>
                </div>
                {approvedRegistrations.length > 0 && (
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-brand-primary/20 text-brand-primary rounded-full hover:bg-brand-primary/30 transition-colors"
                    data-testid="export-csv-btn"
                  >
                    <Download size={16} />
                    Export CSV
                  </button>
                )}
              </div>
              
              {approvedRegistrations.length === 0 ? (
                <div className="p-12 text-center text-text-secondary">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No approved registrations yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-page-bg">
                      <tr>
                        <th className="text-left p-4 caption text-text-secondary">Email</th>
                        <th className="text-left p-4 caption text-text-secondary">Registered At</th>
                        <th className="text-left p-4 caption text-text-secondary">Approved At</th>
                        <th className="w-16 p-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedRegistrations.map((reg) => (
                        <tr key={reg.id} className="border-t border-border-light hover:bg-page-bg/50 group">
                          <td className="p-4 body-small text-text-primary">
                            <div className="flex items-center gap-2">
                              <Check size={14} className="text-green-500" />
                              {reg.email}
                            </div>
                          </td>
                          <td className="p-4 body-small text-text-secondary">
                            {new Date(reg.registered_at).toLocaleString()}
                          </td>
                          <td className="p-4 body-small text-text-secondary">
                            {reg.approved_at ? new Date(reg.approved_at).toLocaleString() : '-'}
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => handleDeleteBeta(reg.id)}
                              disabled={deletingBeta[reg.id]}
                              className="p-2 text-text-secondary hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete beta tester"
                              data-testid={`delete-beta-${reg.id}`}
                            >
                              {deletingBeta[reg.id] ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Rejected Registrations */}
            {rejectedRegistrations.length > 0 && (
              <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
                <div className="p-6 border-b border-border-light flex items-center gap-3">
                  <X className="text-red-500" size={20} />
                  <div>
                    <h2 className="heading-5">Rejected Registrations</h2>
                    <p className="body-small text-text-secondary mt-1">
                      {rejectedRegistrations.length} registrations rejected
                    </p>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-page-bg">
                      <tr>
                        <th className="text-left p-4 caption text-text-secondary">Email</th>
                        <th className="text-left p-4 caption text-text-secondary">Registered At</th>
                        <th className="w-16 p-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejectedRegistrations.map((reg) => (
                        <tr key={reg.id} className="border-t border-border-light hover:bg-page-bg/50 group">
                          <td className="p-4 body-small text-text-primary">{reg.email}</td>
                          <td className="p-4 body-small text-text-secondary">
                            {new Date(reg.registered_at).toLocaleString()}
                          </td>
                          <td className="p-4">
                            <button
                              onClick={() => handleDeleteBeta(reg.id)}
                              disabled={deletingBeta[reg.id]}
                              className="p-2 text-text-secondary hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete registration"
                              data-testid={`delete-rejected-${reg.id}`}
                            >
                              {deletingBeta[reg.id] ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Screenshots Tab */}
        {activeTab === 'screenshots' && (
          <div className="space-y-8">
            {/* Pending Screenshots */}
            <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
              <div className="p-6 border-b border-border-light flex items-center gap-3">
                <Clock className="text-yellow-500" size={20} />
                <div>
                  <h2 className="heading-5">Pending Approval</h2>
                  <p className="body-small text-text-secondary mt-1">
                    {pendingScreenshots.length} screenshots waiting for review
                  </p>
                </div>
              </div>
              
              {pendingScreenshots.length === 0 ? (
                <div className="p-12 text-center text-text-secondary">
                  <Image size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No pending screenshots</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                  {pendingScreenshots.map((screenshot) => (
                    <div key={screenshot.id} className="bg-page-bg rounded-xl overflow-hidden border border-border-light">
                      <div className="aspect-video relative">
                        <img 
                          src={screenshot.image_data} 
                          alt={screenshot.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-4">
                        <p className="body-small text-text-primary font-medium truncate">
                          {screenshot.uploader_name}
                        </p>
                        {screenshot.caption && (
                          <p className="body-small text-text-secondary mt-1 line-clamp-2">
                            {screenshot.caption}
                          </p>
                        )}
                        <p className="caption text-text-secondary mt-2">
                          {new Date(screenshot.uploaded_at).toLocaleString()}
                        </p>
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => handleScreenshotAction(screenshot.id, 'approve')}
                            className="flex-1 flex items-center justify-center gap-1 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                            data-testid={`approve-${screenshot.id}`}
                          >
                            <Check size={14} />
                            Approve
                          </button>
                          <button
                            onClick={() => handleScreenshotAction(screenshot.id, 'reject')}
                            className="flex-1 flex items-center justify-center gap-1 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                            data-testid={`reject-${screenshot.id}`}
                          >
                            <X size={14} />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All Screenshots Overview */}
            <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
              <div className="p-6 border-b border-border-light flex items-center gap-3">
                <Eye className="text-brand-primary" size={20} />
                <div>
                  <h2 className="heading-5">All Screenshots</h2>
                  <p className="body-small text-text-secondary mt-1">
                    {allScreenshots.filter(s => s.status === 'approved').length} approved, {' '}
                    {allScreenshots.filter(s => s.status === 'rejected').length} rejected
                  </p>
                </div>
              </div>
              
              {allScreenshots.length === 0 ? (
                <div className="p-12 text-center text-text-secondary">
                  <Image size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No screenshots uploaded yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
                  {allScreenshots.map((screenshot) => (
                    <div key={screenshot.id} className="relative rounded-lg overflow-hidden border border-border-light group">
                      <div className="aspect-video">
                        <img 
                          src={screenshot.image_data} 
                          alt={screenshot.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                        screenshot.status === 'approved' ? 'bg-green-500 text-white' :
                        screenshot.status === 'rejected' ? 'bg-red-500 text-white' :
                        'bg-yellow-500 text-black'
                      }`}>
                        {screenshot.status}
                      </div>
                      <button
                        onClick={() => handleDeleteScreenshot(screenshot.id)}
                        className="absolute top-2 right-2 p-2 bg-red-500/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        data-testid={`delete-${screenshot.id}`}
                        title="Delete screenshot"
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs truncate">{screenshot.uploader_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Site Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
              <div className="p-6 border-b border-border-light">
                <h2 className="heading-5">Site Content Management</h2>
                <p className="body-small text-text-secondary mt-1">
                  Edit trailer video and images displayed on the website
                </p>
              </div>
              
              <div className="p-6 space-y-6">
                {siteContent.length === 0 ? (
                  <div className="text-center py-8 text-text-secondary">
                    <Settings size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Loading site content...</p>
                  </div>
                ) : (
                  siteContent.map((item) => (
                    <div key={item.key} className="bg-page-bg rounded-xl p-5 border border-border-light">
                      <div className="flex items-center gap-3 mb-4">
                        {getContentIcon(item.key)}
                        <div>
                          <h3 className="body-small font-semibold text-text-primary">{item.label}</h3>
                          <p className="caption text-text-secondary">Key: {item.key}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={editingContent[item.key] || ''}
                          onChange={(e) => setEditingContent(prev => ({ ...prev, [item.key]: e.target.value }))}
                          className="form-input flex-1 text-sm"
                          placeholder="Enter URL..."
                          data-testid={`input-${item.key}`}
                        />
                        <button
                          onClick={() => handleSaveContent(item.key)}
                          disabled={savingContent[item.key] || editingContent[item.key] === item.value}
                          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                            editingContent[item.key] !== item.value
                              ? 'bg-brand-primary text-page-bg hover:bg-brand-primary/90'
                              : 'bg-card-bg text-text-secondary border border-border-light'
                          }`}
                          data-testid={`save-${item.key}`}
                        >
                          {savingContent[item.key] ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Save size={16} />
                          )}
                          Save
                        </button>
                      </div>
                      
                      {/* Preview */}
                      {editingContent[item.key] && (
                        <div className="mt-4">
                          <p className="caption text-text-secondary mb-2">Preview:</p>
                          {item.key.includes('video') ? (
                            <video
                              src={editingContent[item.key]}
                              className="w-full max-w-md h-40 object-cover rounded-lg bg-black"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={editingContent[item.key]}
                              alt={item.label}
                              className="w-full max-w-md h-40 object-cover rounded-lg bg-black"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p className="body-small text-yellow-500">
                <strong>Note:</strong> Changes to site content will take effect after the page is refreshed. Make sure URLs are valid and accessible.
              </p>
            </div>
          </div>
        )}

        {/* Elements Tab */}
        {activeTab === 'elements' && (
          <div className="space-y-6">
            <div className="bg-card-bg rounded-2xl border border-border-light overflow-hidden">
              <div className="p-6 border-b border-border-light">
                <h2 className="heading-5">Element Management</h2>
                <p className="body-small text-text-secondary mt-1">
                  Customize element names, descriptions, and abilities
                </p>
              </div>
              
              <div className="p-6 space-y-8">
                {elements.length === 0 ? (
                  <div className="text-center py-8 text-text-secondary">
                    <Flame size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Loading elements...</p>
                  </div>
                ) : (
                  elements.map((element) => (
                    <div 
                      key={element.id} 
                      className="bg-page-bg rounded-xl p-6 border border-border-light"
                      style={{ borderLeftColor: element.color, borderLeftWidth: '4px' }}
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: element.color + '30' }}
                          >
                            <Flame size={20} style={{ color: element.color }} />
                          </div>
                          <div>
                            <h3 className="heading-5" style={{ color: element.color }}>
                              {editingElements[element.id]?.name || element.name}
                            </h3>
                            <p className="caption text-text-secondary">ID: {element.id}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleSaveElement(element.id)}
                          disabled={savingElement[element.id] || !hasElementChanged(element.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            hasElementChanged(element.id)
                              ? 'bg-brand-primary text-page-bg hover:bg-brand-primary/90'
                              : 'bg-card-bg text-text-secondary border border-border-light'
                          }`}
                          data-testid={`save-element-${element.id}`}
                        >
                          {savingElement[element.id] ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Save size={16} />
                          )}
                          Save Changes
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name & Title */}
                        <div className="space-y-4">
                          <div className="form-group">
                            <label className="form-label">Element Name</label>
                            <input
                              type="text"
                              value={editingElements[element.id]?.name || ''}
                              onChange={(e) => updateElementField(element.id, 'name', e.target.value)}
                              className="form-input"
                              placeholder="e.g., Fire"
                              data-testid={`element-name-${element.id}`}
                            />
                          </div>
                          
                          <div className="form-group">
                            <label className="form-label">Title / Class Name</label>
                            <input
                              type="text"
                              value={editingElements[element.id]?.title || ''}
                              onChange={(e) => updateElementField(element.id, 'title', e.target.value)}
                              className="form-input"
                              placeholder="e.g., Pyromancer"
                              data-testid={`element-title-${element.id}`}
                            />
                          </div>
                          
                          <div className="form-group">
                            <label className="form-label">Color (Hex)</label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={editingElements[element.id]?.color || '#ffffff'}
                                onChange={(e) => updateElementField(element.id, 'color', e.target.value)}
                                className="w-12 h-10 rounded-lg cursor-pointer border border-border-light"
                              />
                              <input
                                type="text"
                                value={editingElements[element.id]?.color || ''}
                                onChange={(e) => updateElementField(element.id, 'color', e.target.value)}
                                className="form-input flex-1"
                                placeholder="#ff4500"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Description */}
                        <div className="form-group">
                          <label className="form-label">Description</label>
                          <textarea
                            value={editingElements[element.id]?.description || ''}
                            onChange={(e) => updateElementField(element.id, 'description', e.target.value)}
                            className="form-input min-h-[140px] resize-none"
                            placeholder="Describe this element's playstyle..."
                            data-testid={`element-desc-${element.id}`}
                          />
                        </div>
                      </div>
                      
                      {/* Abilities */}
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-3">
                          <label className="form-label mb-0">Abilities</label>
                          <button
                            onClick={() => addAbility(element.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-brand-primary/20 text-brand-primary rounded-lg hover:bg-brand-primary/30 transition-colors text-sm"
                            data-testid={`add-ability-${element.id}`}
                          >
                            <Plus size={14} />
                            Add Ability
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          {(editingElements[element.id]?.abilities || []).map((ability, abilityIndex) => (
                            <div key={`${element.id}-ability-${abilityIndex}-${ability.name || abilityIndex}`} className="bg-card-bg rounded-lg p-3 border border-border-light">
                              <div className="flex items-start gap-3">
                                {/* Icon Preview */}
                                <div className="flex-shrink-0">
                                  {ability.icon ? (
                                    <img 
                                      src={ability.icon} 
                                      alt={ability.name}
                                      className="w-10 h-10 rounded-lg object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${ability.icon ? 'hidden' : ''}`}
                                    style={{ backgroundColor: element.color + '30' }}
                                  >
                                    <div 
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: element.color }}
                                    ></div>
                                  </div>
                                </div>
                                
                                {/* Ability Fields */}
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={ability.name || ''}
                                    onChange={(e) => updateAbilityName(element.id, abilityIndex, e.target.value)}
                                    className="form-input w-full"
                                    placeholder="Ability name"
                                    data-testid={`ability-name-${element.id}-${abilityIndex}`}
                                  />
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={ability.icon || ''}
                                      onChange={(e) => updateAbilityIcon(element.id, abilityIndex, e.target.value)}
                                      className="form-input flex-1 text-sm"
                                      placeholder="Icon URL (optional)"
                                      data-testid={`ability-icon-${element.id}-${abilityIndex}`}
                                    />
                                    <button
                                      onClick={() => removeAbility(element.id, abilityIndex)}
                                      className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                      title="Remove ability"
                                    >
                                      <Minus size={16} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {(editingElements[element.id]?.abilities || []).length === 0 && (
                            <p className="text-text-secondary text-sm italic">
                              No abilities added. Click "Add Ability" to create one.
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Image URL */}
                      <div className="mt-6 form-group">
                        <label className="form-label">Image URL (optional)</label>
                        <input
                          type="text"
                          value={editingElements[element.id]?.image || ''}
                          onChange={(e) => updateElementField(element.id, 'image', e.target.value)}
                          className="form-input"
                          placeholder="https://..."
                          data-testid={`element-image-${element.id}`}
                        />
                        {editingElements[element.id]?.image && (
                          <div className="mt-2">
                            <img
                              src={editingElements[element.id].image}
                              alt={element.name}
                              className="w-32 h-20 object-cover rounded-lg"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p className="body-small text-yellow-500">
                <strong>Note:</strong> Changes will be reflected on the website immediately after saving. The element ID cannot be changed.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
